import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import { SITE_URL } from "@/lib/seo";
import { HOME } from "@/constants/testIds";

const steps = [
  { n: "01", t: "Discovery & Direction", d: "We listen. We learn your brand, your customers, and the quiet ambitions behind the business. Out of this conversation, a clear direction." },
  { n: "02", t: "Strategy & Structure", d: "A clear architecture for your story, pages, journeys, and the moments that turn a visitor into an enquiry. Sitemap, wireframes, and a copy approach you can sign off on." },
  { n: "03", t: "Design & Development", d: "Considered, custom design, typeset and built with the same care a luxury studio gives a flagship store. We design and develop in parallel for speed without compromise." },
  { n: "04", t: "Refine & Perfect", d: "We polish. Word by word, pixel by pixel, until every element feels inevitable. Cross-device testing, accessibility passes, and a quiet pre-launch review with you." },
  { n: "05", t: "Launch & Support", d: "We launch with you, and stay on hand to evolve the site as your business grows. Monthly check-ins, small refinements, and a studio you can quietly trust." },
];

export default function Process() {
  const crumbs = [
    { name: "Home", url: `${SITE_URL}/` },
    { name: "Process", url: `${SITE_URL}/process` },
  ];
  return (
    <main className="pt-32 sm:pt-40 bg-lak-bg">
      <Seo
        title="Our Process · From First Conversation to Launch"
        description="Five quiet, considered stages from discovery through to launch. How LAK Atelier designs and builds bespoke websites for independent UK brands."
        keywords="web design process, how web design works, custom website process, bespoke web design UK"
        breadcrumbs={crumbs}
      />
      <section className="lak-section pb-20 sm:pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14">
          <Breadcrumbs items={crumbs} className="mb-8" />
          <Reveal><span className="eyebrow">Process · How we work</span></Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-8 font-serif text-6xl sm:text-7xl lg:text-9xl font-light tracking-tight leading-[0.96] max-w-5xl">
              Thoughtfully crafted.<br />
              <em className="italic text-lak-sage">Strategically built.</em>
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-10 max-w-2xl text-lg text-lak-soft">
              Every project is treated like the only one we are working on, a quiet, considered process
              that respects your time and your brand. Five clear stages, from first conversation to launch.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid={HOME.processSection} className="lak-section pb-32">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 border-t border-black/10">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.06}>
              <div className="group grid grid-cols-12 gap-6 py-12 lg:py-16 border-b border-black/10 cursor-default">
                <div className="col-span-2 lg:col-span-1">
                  <span className="font-serif text-3xl lg:text-4xl italic text-lak-highlight">{s.n}</span>
                </div>
                <div className="col-span-10 lg:col-span-5">
                  <h3 className="font-serif text-3xl lg:text-5xl font-light tracking-tight transition-colors group-hover:text-lak-sage">{s.t}</h3>
                </div>
                <div className="col-span-12 lg:col-span-6">
                  <p className="text-lak-soft text-lg leading-relaxed max-w-xl">{s.d}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="lak-section py-28 bg-lak-secondary">
        <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-14 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight leading-[1.05]">
                Step one starts with <em className="italic text-lak-sage">a short conversation.</em>
              </h2>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4 flex flex-wrap gap-4">
            <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 btn-sage text-[11px] tracking-[0.28em] uppercase">
              Start Your Project <ArrowUpRight size={16} strokeWidth={1.25} />
            </Link>
            <Link to="/services" className="inline-flex items-center gap-2 px-8 py-4 btn-outline text-[11px] tracking-[0.28em] uppercase">
              View Services
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
